const API_BASE = '/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown[];
  };
  meta: {
    timestamp: string;
    requestId: string;
  };
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add dev user header for local development
  if (import.meta.env.DEV) {
    (headers as Record<string, string>)['X-Dev-User'] = 'dev@positioniq.local';
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const json: ApiResponse<T> = await response.json();

  if (!json.success) {
    throw new Error(json.error?.message || 'An error occurred');
  }

  return json.data as T;
}

export const api = {
  // Contracts
  getContracts: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{ contracts: Contract[]; pagination: Pagination }>(`/contracts${query}`);
  },

  getContract: (id: string) => request<Contract>(`/contracts/${id}`),

  // Clients
  getClients: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{ clients: Client[]; pagination: Pagination }>(`/clients${query}`);
  },

  getClient: (id: string) => request<Client>(`/clients/${id}`),

  // Transactions
  getTransactions: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{ transactions: Transaction[]; pagination: Pagination }>(`/transactions${query}`);
  },

  createTransaction: (data: CreateTransactionInput) =>
    request<Transaction>('/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Positions
  getPositions: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{ positions: Position[]; pagination: Pagination }>(`/positions${query}`);
  },

  getPositionSummary: () =>
    request<{ summary: PositionSummary[] }>('/positions/summary'),

  // Alerts
  getAlerts: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{ alerts: Alert[]; pagination: Pagination }>(`/alerts${query}`);
  },

  getAlertSummary: () => request<AlertSummary>('/alerts/summary'),

  acknowledgeAlert: (id: string) =>
    request('/alerts/' + id + '/acknowledge', { method: 'POST' }),

  // Import
  uploadFile: async (file: File, clientId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (clientId) formData.append('clientId', clientId);

    const response = await fetch(`${API_BASE}/import/upload`, {
      method: 'POST',
      body: formData,
      headers: import.meta.env.DEV ? { 'X-Dev-User': 'dev@positioniq.local' } : {},
    });

    const json = await response.json();
    if (!json.success) throw new Error(json.error?.message);
    return json.data as ImportJob;
  },

  getImportJobs: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{ jobs: ImportJob[]; pagination: Pagination }>(`/import/jobs${query}`);
  },

  getImportJob: (id: string) => request<ImportJob>(`/import/jobs/${id}`),
};

// Types
interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface Contract {
  id: string;
  productCode: string;
  symbol: string;
  exchange: string;
  deliveryMonth: string;
  spotMonthLimit: number;
  singleMonthLimit: number;
  allMonthsLimit: number;
  isActive: boolean;
}

interface Client {
  id: string;
  name: string;
  fcmAccountId?: string;
  clientType: string;
  hasHedgeExemption: boolean;
  isActive: boolean;
}

interface Transaction {
  id: string;
  clientId: string;
  contractId: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  tradeDate: string;
  source: string;
  status: string;
  client?: { id: string; name: string };
  contract?: { id: string; symbol: string; productCode: string };
}

interface CreateTransactionInput {
  clientId: string;
  contractId: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  tradeDate: string;
}

interface Position {
  id: string;
  clientId: string;
  contractId: string;
  netQuantity: number;
  spotMonthUtilization: number;
  singleMonthUtilization: number;
  allMonthsUtilization: number;
  status: string;
  client?: { id: string; name: string };
  contract?: { id: string; symbol: string; productCode: string };
}

interface PositionSummary {
  clientId: string;
  clientName: string;
  totalPositions: number;
  maxUtilization: number;
  status: string;
  breachCount: number;
  warningCount: number;
}

interface Alert {
  id: string;
  clientId: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  isAcknowledged: boolean;
  createdAt: string;
}

interface AlertSummary {
  total: number;
  unacknowledged: number;
  bySeverity: { info: number; warning: number; critical: number };
}

interface ImportJob {
  id: string;
  fileName: string;
  fileSize: number;
  status: string;
  totalRecords: number;
  successCount: number;
  errorCount: number;
  uploadedAt: string;
}

export type {
  Contract,
  Client,
  Transaction,
  Position,
  PositionSummary,
  Alert,
  AlertSummary,
  ImportJob,
  Pagination,
};
